// ./src/components/features/Themes/ThemeFormModal/ThemeElementControl.tsx
import React from "react";
import { Stack, Text, Switch } from "@mantine/core";
import {
  ColorSelector,
  FontSizeSelector,
  FontWeightSelector,
  FontFamilySelector,
  HeightSelector,
  GapSelector,
  DateTimeFormatSelector,
} from "@/components/generic";
import { ComponentsEditor } from "../../DisplayLayouts/ComponentsEditor";
import { ZonesEditor } from "../../DisplayLayouts/ZonesEditor";

export interface ThemeElementConfig {
  label: string;
  accessAs: string;
  component:
    | "ColorSelector"
    | "FontSizeSelector"
    | "FontWeightSelector"
    | "FontFamilySelector"
    | "Switch"
    | "HeightSelector"
    | "GapSelector"
    | "DateTimeFormatSelector"
    | "ComponentsEditor"
    | "ZonesEditor";
  default: any;
  allowCustom?: boolean;
  description?: string;
  options?: any;
}

export interface ThemeElementControlProps {
  /** Element configuration from themeEditorUiConfig or layoutEditorUiConfig */
  elementConfig: ThemeElementConfig;

  /** Current value for this element */
  value: any;

  /** Callback when value changes */
  onChange: (value: any) => void;

  /** Size of the control */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const ThemeElementControl: React.FC<ThemeElementControlProps> = ({
  elementConfig,
  value,
  onChange,
  size = "sm",
}) => {
  const {
    label,
    component,
    default: defaultValue,
    allowCustom,
    description,
    options = {},
  } = elementConfig;

  // Use default value if current value is not set
  const currentValue = value ?? defaultValue;

  // Render the appropriate component based on type
  const renderControl = () => {
    switch (component) {
      case "ColorSelector":
        return (
          <ColorSelector
            value={currentValue}
            onChange={onChange}
            placeholder="Select color"
            {...options}
          />
        );

      case "FontSizeSelector":
        return (
          <FontSizeSelector
            value={currentValue}
            onChange={onChange}
            size={size}
            allowCustom={allowCustom}
            placeholder="Select font size"
            required
            {...options}
          />
        );

      case "FontWeightSelector":
        return (
          <FontWeightSelector
            value={currentValue}
            onChange={onChange}
            size={size}
            placeholder="Select font weight"
            required
            {...options}
          />
        );

      case "FontFamilySelector":
        return (
          <FontFamilySelector
            value={currentValue}
            onChange={onChange}
            size={size}
            allowCustom={allowCustom}
            placeholder="Select font family"
            required
            {...options}
          />
        );

      case "Switch":
        return (
          <Switch
            checked={currentValue}
            onChange={(event) => onChange(event.currentTarget.checked)}
            size={size}
            {...options}
          />
        );

      case "HeightSelector":
        return (
          <HeightSelector
            value={currentValue}
            onChange={onChange}
            size={size}
            placeholder="Select height"
            {...options}
          />
        );

      case "GapSelector":
        return (
          <GapSelector
            value={currentValue}
            onChange={onChange}
            size={size}
            placeholder="Select gap"
            {...options}
          />
        );

      case "DateTimeFormatSelector":
        return (
          <DateTimeFormatSelector
            value={currentValue}
            onChange={onChange}
            size={size}
            placeholder="Select format"
            {...options}
          />
        );

      case "ComponentsEditor":
        return (
          <ComponentsEditor
            value={currentValue}
            onChange={onChange}
            {...options}
          />
        );

      case "ZonesEditor":
        return (
          <ZonesEditor value={currentValue} onChange={onChange} {...options} />
        );

      default:
        console.warn(`Unknown component type: ${component}`);
        return (
          <Text size="sm" c="dimmed">
            Unknown control type: {component}
          </Text>
        );
    }
  };

  return (
    <Stack gap="xs">
      <div>
        <Text size="sm" fw={500}>
          {label}
        </Text>
        {description && (
          <Text size="xs" c="dimmed" mt={2}>
            {description}
          </Text>
        )}
      </div>
      {renderControl()}
    </Stack>
  );
};
